import Highcharts from "./highchartsInit";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

export { Highcharts };

/** Clayo chart colors — only hues used on clayo.io frontend (Avada --awb-color* theme vars) */
export const THEME = {
  bg: "#0b1220",
  card: "#111827",
  cardBorder: "#1e293b",
  text: "#e2e8f0",
  muted: "#94a3b8",
  primary: "#00C074",
  success: "#006766",
  warning: "#89D677",
  danger: "#006B44",
  purple: "#006B44",
  palette: ["#00C074", "#006766", "#006B44", "#89D677", "#C2DAE1"],
};

export const KPI_SPARK_COLOR = "#00C074";

export const formatNumber = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num)) return "0";
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(1)}K`;
  return num.toLocaleString();
};

export const formatPct = (rate) => `${(Number(rate) * 100).toFixed(1)}%`;

export const formatCurrency = (value, currency = "EUR") => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(num);
};

export const formatRelativeTime = (value) => {
  if (!value) return "—";
  const d = dayjs(value);
  return d.isValid() ? d.fromNow() : "—";
};

export const getInitials = (firstName, lastName, fallback = "?") => {
  const parts = [firstName, lastName]
    .filter(Boolean)
    .map((s) => String(s).trim()[0])
    .filter(Boolean);
  if (parts.length) return parts.join("").slice(0, 2).toUpperCase();
  return String(fallback || "?")
    .slice(0, 2)
    .toUpperCase();
};

export const buildTelHref = (mobile, countryCalling) => {
  const digits = String(mobile || "").replace(/\D/g, "");
  if (!digits) return null;
  const code = String(countryCalling || "").replace(/\D/g, "");
  const full = code && !digits.startsWith(code) ? `${code}${digits}` : digits;
  return `tel:+${full.replace(/^\+/, "")}`;
};

export const buildMailtoHref = (email, { name, product, step } = {}) => {
  if (!email) return null;
  const subject = encodeURIComponent(`Complete your ${product || "investment"} with Clayo`);
  const bodyLines = [
    name ? `Hi ${name},` : "Hi,",
    "",
    `I noticed you started exploring ${product || "our product"} but didn't finish${
      step ? ` at the ${step} step` : ""
    }.`,
    "I'm happy to help you complete your investment or answer any questions.",
    "",
    "Best regards",
  ];
  const body = encodeURIComponent(bodyLines.join("\n"));
  return `mailto:${email}?subject=${subject}&body=${body}`;
};

export const formatHotLeadStep = (step) => {
  const raw = String(step || "").trim();
  if (!raw || raw === "unknown") return "unknown step";
  return raw.replace(/_/g, " ");
};

/** Map empty / unknown PostHog product names to display label. */
export const formatProductLabel = (name) => {
  const raw = String(name || "").trim();
  if (!raw || /^unknown(\s+product)?$/i.test(raw)) return "GM Next";
  const lower = raw.toLowerCase().replace(/\s+/g, " ");
  if (lower === "gm next") return "GM Next";
  if (lower === "gm open") return "GM Open";
  if (lower.startsWith("gm ")) {
    const rest = lower
      .slice(3)
      .split(/\s+/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
    return `GM ${rest}`;
  }
  return raw;
};

export const mergeProductRows = (rows = []) => {
  const map = new Map();
  rows.forEach((row) => {
    const product = formatProductLabel(row.product);
    const key = product.toLowerCase();
    if (!map.has(key)) {
      map.set(key, { ...row, product, views: 0, cta_clicks: 0, investments: 0 });
    }
    const item = map.get(key);
    item.views += Number(row.views) || 0;
    item.cta_clicks += Number(row.cta_clicks) || 0;
    item.investments += Number(row.investments) || 0;
  });
  return Array.from(map.values())
    .map((row) => ({
      ...row,
      conv_rate: row.views > 0 ? Number((row.investments / row.views).toFixed(4)) : 0,
    }))
    .sort((a, b) => b.views - a.views || b.investments - a.investments);
};

export const formatDuration = (sec) => {
  const s = Number(sec) || 0;
  if (s < 60) return `${Math.round(s)}s`;
  return `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`;
};

/** Compact duration for KPI cards — max decimal places on minutes/seconds. */
export const formatDurationShort = (sec, maxDecimals = 2) => {
  const s = Number(sec);
  if (!Number.isFinite(s) || s <= 0) return "—";
  if (s < 60) return `${Number(s.toFixed(maxDecimals))}s`;
  if (s < 3600) return `${Number((s / 60).toFixed(maxDecimals))}m`;
  const hours = Math.floor(s / 3600);
  const minutes = Number(((s % 3600) / 60).toFixed(maxDecimals));
  return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
};

const csvCell = (value) => {
  const text = value == null ? "" : String(value);
  if (/[",\n\r]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
  return text;
};

export const getEventVersion = (properties = {}) =>
  properties.app_version || properties.$app_version || properties.version || "";

export const getEventPlatform = (properties = {}) =>
  properties.platform || properties.$os_name || properties.$os || "";

export const getEventLocation = (properties = {}) => {
  const city = properties.city || properties.$geoip_city_name || "";
  const country = properties.country || properties.$geoip_country_name || "";
  return [city, country].filter(Boolean).join(", ");
};

export const googleMapsSearchUrl = ({ lat, lng, label } = {}) => {
  if (lat != null && lng != null && Number.isFinite(Number(lat)) && Number.isFinite(Number(lng))) {
    return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
  }
  if (label) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(label)}`;
  }
  return null;
};

export const googleMapsEmbedUrl = (lat, lng) => {
  if (lat == null || lng == null) return null;
  const la = Number(lat);
  const ln = Number(lng);
  if (!Number.isFinite(la) || !Number.isFinite(ln)) return null;
  return `https://maps.google.com/maps?q=${la},${ln}&z=11&output=embed`;
};

export const exportEventsCsv = (events, filename = "events.csv") => {
  const headers = [
    "timestamp",
    "event",
    "screen",
    "version",
    "platform",
    "location",
    "details",
  ];
  const rows = (events || []).map((ev) => {
    const p = ev.properties || {};
    return [
      ev.timestamp || "",
      ev.event || "",
      p.page_name || p.screen_name || "",
      getEventVersion(p),
      getEventPlatform(p),
      getEventLocation(p),
      [
        p.step_name && `step: ${p.step_name}`,
        p.form_name && `form: ${p.form_name}`,
        p.feature_name && `feature: ${p.feature_name}`,
        p.product_name && `product: ${p.product_name}`,
        p.error_message && `error: ${p.error_message}`,
      ]
        .filter(Boolean)
        .join(" · "),
    ]
      .map(csvCell)
      .join(",");
  });
  const csv = [headers.join(","), ...rows].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const darkBase = (overrides = {}) => {
  const {
    chart: chartOverrides = {},
    xAxis: xAxisOverrides,
    yAxis: yAxisOverrides,
    legend: legendOverrides,
    tooltip: tooltipOverrides,
    title: titleOverrides,
    ...restOverrides
  } = overrides;

  return {
    ...restOverrides,
    chart: {
      backgroundColor: "transparent",
      plotBackgroundColor: "transparent",
      style: { fontFamily: "inherit", color: THEME.text },
      ...chartOverrides,
      backgroundColor: "transparent",
      plotBackgroundColor: "transparent",
    },
    credits: { enabled: false },
    title: titleOverrides !== undefined ? titleOverrides : { text: null },
    legend: {
      itemStyle: { color: THEME.muted },
      itemHoverStyle: { color: THEME.text },
      ...legendOverrides,
    },
    xAxis: {
      labels: { style: { color: THEME.muted } },
      lineColor: THEME.cardBorder,
      tickColor: THEME.cardBorder,
      gridLineColor: THEME.cardBorder,
      ...(xAxisOverrides || {}),
    },
    yAxis: {
      labels: { style: { color: THEME.muted } },
      gridLineColor: "#1e293b",
      title: { style: { color: THEME.muted } },
      ...(yAxisOverrides || {}),
    },
    tooltip: {
      backgroundColor: "#1e293b",
      borderColor: "#334155",
      style: { color: THEME.text },
      ...tooltipOverrides,
    },
  };
};

export const sparklineOptions = (data, color = KPI_SPARK_COLOR) =>
  darkBase({
    chart: {
      type: "areaspline",
      height: 32,
      width: null,
      spacing: [0, 0, 0, 0],
      margin: [0, 0, 0, 0],
      style: { fontFamily: "inherit" },
    },
    xAxis: { visible: false },
    yAxis: { visible: false },
    legend: { enabled: false },
    tooltip: { enabled: false },
    plotOptions: {
      areaspline: {
        fillOpacity: 0.14,
        lineWidth: 1.5,
        marker: { enabled: false },
        states: { hover: { enabled: false } },
      },
      series: { animation: false },
    },
    series: [{ data: data?.length ? data : [0], color, fillColor: color }],
  });

/** Larger trend chart for KPI detail modal — line chart on white cards, not filled spark bars */
export const kpiDetailTrendOptions = (data, color = KPI_SPARK_COLOR) => {
  const values = (Array.isArray(data) && data.length ? data : [0]).map((v) => Number(v) || 0);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min;
  const pad = range === 0 ? Math.max(Math.abs(max) * 0.15, 1) : range * 0.2;

  return {
    chart: {
      type: "spline",
      backgroundColor: "transparent",
      height: 88,
      spacing: [4, 4, 2, 0],
      margin: [6, 8, 18, 40],
      style: { fontFamily: "inherit" },
    },
    credits: { enabled: false },
    title: { text: null },
    legend: { enabled: false },
    xAxis: {
      visible: true,
      labels: { enabled: false },
      lineWidth: 0,
      tickLength: 0,
    },
    yAxis: {
      title: { text: null },
      labels: {
        style: { color: "#64748b", fontSize: "9px" },
        formatter() {
          return formatNumber(this.value);
        },
      },
      gridLineColor: "#e2e8f0",
      min: min - pad,
      max: max + pad,
      startOnTick: false,
      endOnTick: false,
    },
    tooltip: {
      backgroundColor: "#fff",
      borderColor: "#e2e8f0",
      style: { color: "#333", fontSize: "11px" },
      formatter() {
        return `<b>${formatNumber(this.y)}</b>`;
      },
    },
    plotOptions: {
      spline: {
        lineWidth: 2,
        marker: {
          enabled: values.length <= 14,
          radius: 2,
          fillColor: color,
          lineWidth: 0,
        },
        states: { hover: { lineWidth: 2.5 } },
      },
      series: { animation: false },
    },
    series: [{ data: values, color, name: "Value" }],
  };
};

export const lineChartOptions = ({ categories, series, yTitle = "Count" }) =>
  darkBase({
    chart: { height: 260 },
    xAxis: { categories, crosshair: true },
    yAxis: { title: { text: yTitle }, min: 0 },
    tooltip: { shared: true },
    legend: { enabled: series.length > 1 },
    plotOptions: { line: { marker: { enabled: true, radius: 4 } } },
    series,
  });

export const donutOptions = (items, title) => {
  const total = items.reduce((s, i) => s + (i.y || i.users || i.count || 0), 0) || 1;
  return darkBase({
    chart: { type: "pie", height: 220 },
    plotOptions: {
      pie: {
        innerSize: "62%",
        dataLabels: {
          enabled: true,
          format: "<b>{point.name}</b> {point.percentage:.1f}%",
          style: { color: THEME.muted, textOutline: "none", fontSize: "10px" },
        },
      },
    },
    series: [
      {
        name: title,
        data: items.map((item, i) => ({
          name: item.label || item.name,
          y: item.users ?? item.count ?? item.y ?? 0,
          color: THEME.palette[i % THEME.palette.length],
        })),
      },
    ],
    tooltip: {
      pointFormatter() {
        return `<b>${this.y}</b> (${((this.y / total) * 100).toFixed(1)}%)`;
      },
    },
  });
};

export const barChartOptions = ({ categories, data, color = THEME.primary, horizontal = true }) =>
  darkBase({
    chart: { type: horizontal ? "bar" : "column", height: Math.max(180, categories.length * 28) },
    xAxis: { categories },
    yAxis: { min: 0, title: { text: null } },
    legend: { enabled: false },
    plotOptions: { bar: { borderRadius: 3, dataLabels: { enabled: false } } },
    series: [{ name: "Count", data, color }],
  });

export const funnelChartOptions = (steps, { seriesName = "Users", light = false } = {}) => {
  const labelStyle = {
    color: light ? "#333333" : THEME.text,
    textOutline: "none",
    fontSize: steps.length > 5 ? "10px" : "11px",
    fontWeight: "500",
  };

  return darkBase({
    chart: {
      type: "funnel",
      height: 280,
      marginRight: light ? 115 : 100,
      style: { fontFamily: "inherit", color: light ? "#333333" : THEME.text },
    },
    tooltip: light
      ? {
          backgroundColor: "#ffffff",
          borderColor: "#C2DAE1",
          style: { color: "#333333" },
        }
      : undefined,
    plotOptions: {
      series: {
        dataLabels: {
          enabled: true,
          format: "<b>{point.name}</b> ({point.y:,.0f})",
          style: labelStyle,
          connectorColor: light ? "#64748b" : THEME.muted,
          softConnector: true,
          distance: 12,
          crop: false,
          overflow: "allow",
        },
        center: ["40%", "50%"],
        neckWidth: "30%",
        neckHeight: "25%",
        width: "70%",
      },
    },
    legend: { enabled: false },
    series: [
      {
        name: seriesName,
        data: steps.map((s, i) => ({
          name: s.step || s.name,
          y: s.count,
          color: THEME.palette[i % THEME.palette.length],
        })),
      },
    ],
  });
};

export const retentionHeatmapOptions = (cohorts, periodLabel = "Week") => {
  const maxPeriods = Math.max(0, ...cohorts.map((c) => (c.values || []).length));
  const xCategories = Array.from({ length: maxPeriods }, (_, i) =>
    i === 0 ? "Cohort" : `${periodLabel} ${i}`
  );
  const yCategories = cohorts.map((c) => c.cohort_date || "").reverse();
  const data = [];
  cohorts.forEach((cohort, yIndex) => {
    const y = yCategories.length - 1 - yIndex;
    (cohort.values || []).forEach((v, xIndex) => {
      const rate = typeof v.rate === "number" ? v.rate : Number(v.rate);
      data.push([xIndex, y, rate > 1 ? rate : rate * 100]);
    });
  });

  return darkBase({
    chart: { type: "heatmap", height: Math.max(220, yCategories.length * 32 + 60) },
    xAxis: { categories: xCategories },
    yAxis: { categories: yCategories, title: null, reversed: true },
    colorAxis: {
      min: 0,
      max: 100,
      minColor: "#1e293b",
      maxColor: THEME.primary,
    },
    legend: { align: "right", layout: "vertical", margin: 0 },
    series: [
      {
        name: "Retention",
        borderWidth: 1,
        borderColor: THEME.cardBorder,
        data,
        dataLabels: {
          enabled: true,
          color: THEME.text,
          style: { textOutline: "none", fontSize: "10px" },
          formatter() {
            const v = this.point.value;
            return v != null ? `${Number(v).toFixed(0)}%` : "";
          },
        },
      },
    ],
  });
};

export const dataQualityDonut = ({ good, dropped, duplicate, goodPct }) =>
  darkBase({
    chart: { type: "pie", height: 200 },
    plotOptions: {
      pie: {
        innerSize: "70%",
        dataLabels: { enabled: false },
      },
    },
    title: {
      text: `${((goodPct ?? 0) * 100).toFixed(1)}%`,
      align: "center",
      verticalAlign: "middle",
      y: 8,
      style: { color: THEME.text, fontSize: "22px", fontWeight: "700" },
    },
    subtitle: {
      text: "Good Events",
      align: "center",
      verticalAlign: "middle",
      y: 28,
      style: { color: THEME.muted, fontSize: "11px" },
    },
    series: [
      {
        data: [
          { name: "Good events", y: good, color: THEME.success },
          { name: "Dropped", y: dropped, color: THEME.danger },
          { name: "Duplicate", y: duplicate, color: THEME.warning },
        ].filter((d) => d.y > 0),
      },
    ],
  });

export const geoMapBubbleOptions = (points = []) =>
  darkBase({
    chart: { type: "scatter", height: 420 },
    xAxis: { title: { text: "Longitude" } },
    yAxis: { title: { text: "Latitude" } },
    legend: { enabled: false },
    tooltip: {
      headerFormat: "",
      pointFormat: "<b>{point.name}</b><br/>Users: {point.users}",
    },
    plotOptions: {
      scatter: {
        marker: {
          symbol: "circle",
          fillColor: THEME.primary,
          lineWidth: 1,
          lineColor: THEME.cardBorder,
        },
      },
    },
    series: [
      {
        name: "Locations",
        data: points.map((p) => ({
          x: p.lng,
          y: p.lat,
          users: p.users,
          name: [p.city, p.country].filter(Boolean).join(", "),
          marker: {
            radius: Math.min(22, Math.max(5, Math.sqrt(Math.max(p.users, 1)) * 4)),
          },
        })),
        color: THEME.primary,
      },
    ],
  });
